import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Search, ArrowLeft, X, ChevronRight, Home, Bell } from "lucide-react";
import api from "@/lib/api";

const fmtPrice = (p) => `$${Number(p).toLocaleString("es-CO")}`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (path) => path ? `${BACKEND_URL}/api/files/${path}` : null;

// Optimize Google Drive URLs to use thumbnail endpoint
const optimizeImgUrl = (url, size = 400) => {
  if (!url) return null;
  if (url.includes("drive.google.com")) {
    let fileId = null;
    const m1 = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    const m2 = url.match(/id=([a-zA-Z0-9_-]+)/);
    const m3 = url.match(/thumbnail\?id=([a-zA-Z0-9_-]+)/);
    if (m3) fileId = m3[1];
    else if (m1) fileId = m1[1];
    else if (m2) fileId = m2[1];
    if (fileId) return `https://drive.google.com/thumbnail?id=${fileId}&sz=w${size}`;
  }
  return url;
};

const itemImg = (item, size = 400) => {
  if (item.image_path) return imgUrl(item.image_path);
  return optimizeImgUrl(item.image_url, size);
};

function UnitsBadge({ units }) {
  if (!units) return null;
  const n = parseInt(units);
  if (isNaN(n) || n <= 1) return null;
  return <span title={`${units} unidades`} style={{ fontSize: "0.65rem", fontWeight: 700, color: "#2E7D32", background: "#E8F5E9", padding: "1px 6px", borderRadius: "8px", marginLeft: "6px", cursor: "default" }}>{units}</span>;
}

// Inject bell animation styles once
if (!document.getElementById("bell-anim-styles")) {
  const style = document.createElement("style");
  style.id = "bell-anim-styles";
  style.textContent = `
    @keyframes bellRing {
      0% { transform: rotate(0deg) scale(1); }
      10% { transform: rotate(14deg) scale(1.2); }
      20% { transform: rotate(-14deg) scale(1.2); }
      30% { transform: rotate(10deg) scale(1.15); }
      40% { transform: rotate(-10deg) scale(1.15); }
      50% { transform: rotate(6deg) scale(1.1); }
      60% { transform: rotate(-6deg) scale(1.1); }
      70% { transform: rotate(2deg) scale(1.05); }
      80% { transform: rotate(-2deg) scale(1.05); }
      100% { transform: rotate(0deg) scale(1); }
    }
    @keyframes pushSlideIn {
      0% { transform: translateY(-100%); opacity: 0; }
      100% { transform: translateY(0); opacity: 1; }
    }
    @keyframes pushSlideOut {
      0% { transform: translateY(0); opacity: 1; }
      100% { transform: translateY(-100%); opacity: 0; }
    }
    .bell-ring { animation: bellRing 0.8s ease-in-out; }
    .push-toast-in { animation: pushSlideIn 0.4s ease-out; }
    .push-toast-out { animation: pushSlideOut 0.4s ease-in forwards; }
  `;
  document.head.appendChild(style);
}

function PushToastBanner({ toast: t, onDismiss }) {
  const [exiting, setExiting] = useState(false);
  const dismiss = () => { setExiting(true); setTimeout(onDismiss, 400); };
  if (!t) return null;
  return (
    <div style={{ position: "fixed", top: "12px", left: "50%", transform: "translateX(-50%)", zIndex: 60, width: "calc(100% - 32px)", maxWidth: "420px" }}
      className={exiting ? "push-toast-out" : "push-toast-in"} data-testid="push-toast">
      <div onClick={dismiss} style={{
        background: "white", borderRadius: "14px", padding: "14px 16px",
        display: "flex", alignItems: "center", gap: "12px", cursor: "pointer",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)",
        border: "1px solid #E6DFD0"
      }}>
        <div style={{
          width: "40px", height: "40px", borderRadius: "50%", flexShrink: 0,
          background: "#FFF5E6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem"
        }}>{t.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#2A2A2A" }}>{t.title}</div>
          {t.description && <div style={{ fontSize: "0.75rem", color: "#8B7D6B", marginTop: "1px" }}>{t.description}</div>}
        </div>
        <X style={{ width: "16px", height: "16px", color: "#A89F9A", flexShrink: 0 }} />
      </div>
    </div>
  );
}

function MenuItemIcon({ icon, imagePath, imageUrl, sizeClass = "text-3xl", imgClass = "w-12 h-12" }) {
  const src = imagePath ? imgUrl(imagePath) : optimizeImgUrl(imageUrl, 200);
  if (src) return <img src={src} alt="" loading="lazy" decoding="async" className={`${imgClass} rounded-lg object-cover flex-shrink-0`} />;
  return <span className={`${sizeClass} flex-shrink-0`}>{icon || "🍽️"}</span>;
}

const DEFAULT_SECTION_IMAGE = "https://images.unsplash.com/photo-1592064064956-30807b590c08?w=600&h=400&fit=crop";

const getCatImage = (cat) => {
  if (cat.image_path) return imgUrl(cat.image_path);
  if (cat.image_url) return optimizeImgUrl(cat.image_url, 600);
  return DEFAULT_SECTION_IMAGE;
};

function injectGTM(containerId) {
  if (!containerId || document.getElementById("gtm-script")) return;
  const script = document.createElement("script");
  script.id = "gtm-script";
  script.async = true;
  script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${containerId}');`;
  document.head.appendChild(script);
}

function preloadFont(fontFamily) {
  if (!fontFamily || fontFamily === "Manrope") return; // Manrope already loaded globally
  const id = `font-${fontFamily.replace(/\s/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@400;600;700;800&display=swap`;
  document.head.appendChild(link);
}

function SkeletonMenu() {
  return (
    <div style={{ background: "#F5EFE0", minHeight: "100vh", padding: "0" }} data-testid="public-menu-loading">
      <div style={{ background: "#3E1F0D", height: "140px", borderRadius: "0 0 50% 50% / 0 0 40px 40px" }} />
      <div style={{ maxWidth: "600px", margin: "20px auto", padding: "0 16px" }}>
        <div style={{ height: "48px", borderRadius: "12px", background: "#E6DFD0", marginBottom: "16px" }} />
        <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
          {[1,2,3,4].map(i => <div key={i} style={{ height: "36px", width: "90px", borderRadius: "18px", background: "#E6DFD0", flexShrink: 0 }} />)}
        </div>
        {[1,2,3].map(i => (
          <div key={i} style={{ background: "white", borderRadius: "12px", padding: "16px", marginBottom: "12px", display: "flex", gap: "14px" }}>
            <div style={{ width: "72px", height: "72px", borderRadius: "10px", background: "#E6DFD0", flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ height: "16px", width: "60%", borderRadius: "4px", background: "#E6DFD0", marginBottom: "8px" }} />
              <div style={{ height: "12px", width: "100%", borderRadius: "4px", background: "#E6DFD0", marginBottom: "4px" }} />
              <div style={{ height: "12px", width: "40%", borderRadius: "4px", background: "#E6DFD0" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PublicMenu() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get("tab"); // "principal" or "happy_hour"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [liveNotifications, setLiveNotifications] = useState([]);
  const [bellBounce, setBellBounce] = useState(false);
  const [pushToast, setPushToast] = useState(null);
  const prevCountRef = useRef(0);

  // Initial load
  useEffect(() => {
    api.public.getMenu(slug)
      .then((r) => {
        setData(r.data);
        const notifs = r.data.notifications || [];
        setLiveNotifications(notifs);
        prevCountRef.current = notifs.length;
        const rest = r.data.restaurant;
        if (rest?.gtm_container_id) setTimeout(() => injectGTM(rest.gtm_container_id), 100);
        if (rest?.font_family) preloadFont(rest.font_family);
        if (rest?.seo_index === false) {
          let meta = document.querySelector('meta[name="robots"]');
          if (!meta) { meta = document.createElement("meta"); meta.name = "robots"; document.head.appendChild(meta); }
          meta.content = "noindex, nofollow";
        } else {
          let meta = document.querySelector('meta[name="robots"]');
          if (meta) meta.content = "index, follow";
        }
        document.title = "Menú";
      })
      .catch(() => setError("Menu no encontrado"))
      .finally(() => setLoading(false));
  }, [slug]);

  // Real-time notification polling every 10s
  useEffect(() => {
    if (!slug || error) return;
    const interval = setInterval(() => {
      api.public.getNotifications(slug)
        .then((r) => {
          const newNotifs = r.data;
          // Detect new notifications
          if (newNotifs.length > prevCountRef.current) {
            // Find the newest notification
            const newOnes = newNotifs.filter(n => !liveNotifications.find(o => o.id === n.id));
            const latest = newOnes[0] || newNotifs[0];
            // Trigger bell bounce
            setBellBounce(true);
            setTimeout(() => setBellBounce(false), 2000);
            // Show push toast
            if (latest) {
              setPushToast({ icon: latest.icon || "📢", title: latest.title, description: latest.description });
              setTimeout(() => setPushToast(null), 5000);
            }
          }
          prevCountRef.current = newNotifs.length;
          setLiveNotifications(newNotifs);
        })
        .catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, [slug, error, liveNotifications]);

  if (loading) return <SkeletonMenu />;

  if (error) return (
    <div style={{ background: "#F5EFE0", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }} data-testid="public-menu-error">
      <div style={{ textAlign: "center" }}>
        <p style={{ fontSize: "3rem", fontWeight: 700, color: "#D4772C" }}>404</p>
        <p style={{ color: "#8B7D6B" }}>{error}</p>
      </div>
    </div>
  );

  if (!data) return null;
  const { restaurant, notifications: initNotifs } = data;
  const hasHH = data.has_happy_hour;
  // URL param takes priority: ?tab=principal or ?tab=happy_hour
  const defaultTab = urlTab === "happy_hour" ? "happy_hour" : urlTab === "principal" ? "main" : (data.default_tab || "main");
  const template = restaurant.template_slug || "classic";

  const sharedProps = {
    restaurant, notifications: liveNotifications, bellBounce, pushToast,
    onDismissToast: () => setPushToast(null),
    mainCategories: data.main?.categories || [], mainItems: data.main?.items || [],
    hhCategories: data.happy_hour?.categories || [], hhItems: data.happy_hour?.items || [],
    hasHH, defaultTab
  };

  if (template === "modern") return <ModernTemplate {...sharedProps} />;
  return <ClassicTemplate {...sharedProps} />;
}

// ==================== MENU TAB SWITCHER ====================
function MenuTabSwitcher({ activeTab, onSwitch, hasHH, accentColor, style = "light" }) {
  if (!hasHH) return null;
  const tabs = [
    { key: "main", label: "PRINCIPAL" },
    { key: "happy_hour", label: "HAPPY HOUR" },
  ];
  const isLight = style === "light";
  return (
    <div style={{
      display: "flex", justifyContent: "center", gap: "0",
      background: isLight ? "#E6DFD0" : "rgba(255,255,255,0.1)",
      borderRadius: "12px", padding: "4px", maxWidth: "320px", margin: "0 auto"
    }} data-testid="menu-tabs">
      {tabs.map(t => (
        <button key={t.key} onClick={() => onSwitch(t.key)}
          style={{
            flex: 1, padding: "10px 16px", borderRadius: "10px", border: "none",
            cursor: "pointer", fontSize: "0.8rem", fontWeight: 800,
            letterSpacing: "0.5px", transition: "all 0.2s",
            background: activeTab === t.key ? (isLight ? "white" : "rgba(255,255,255,0.2)") : "transparent",
            color: activeTab === t.key ? (accentColor || "#D4772C") : (isLight ? "#8B7D6B" : "rgba(255,255,255,0.5)"),
            boxShadow: activeTab === t.key ? "0 2px 8px rgba(0,0,0,0.08)" : "none"
          }}
          data-testid={`menu-tab-${t.key}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ==================== HAPPY HOUR COUNTDOWN BANNER ====================
function HappyHourBanner({ restaurant, activeTab }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  if (activeTab !== "happy_hour") return null;
  const hh = restaurant.happy_hour_schedule;
  if (!hh || !hh.days || !hh.start_time || !hh.end_time) return null;

  // Colombia UTC-5
  const coNow = new Date(now.getTime() + (now.getTimezoneOffset() * 60000) - (5 * 3600000));
  const dayMap = { "lun": 1, "mar": 2, "mie": 3, "jue": 4, "vie": 5, "sab": 6, "dom": 0 };
  const dayNames = { "lun": "Lunes", "mar": "Martes", "mie": "Miercoles", "jue": "Jueves", "vie": "Viernes", "sab": "Sabado", "dom": "Domingo" };
  const today = coNow.getDay();
  const [sh, sm] = hh.start_time.split(":").map(Number);
  const [eh, em] = hh.end_time.split(":").map(Number);
  const currentMins = coNow.getHours() * 60 + coNow.getMinutes();
  const startMins = sh * 60 + sm;
  const endMins = eh * 60 + em;
  const isHHDay = hh.days.some(d => dayMap[d] === today);
  const isActive = isHHDay && currentMins >= startMins && currentMins <= endMins;

  if (isActive) return null; // Don't show banner when HH is active

  // Calculate countdown to next HH
  let countdownText = "";
  if (isHHDay && currentMins < startMins) {
    // Today, before start
    const diffSecs = (startMins - currentMins) * 60 - coNow.getSeconds();
    const hrs = Math.floor(diffSecs / 3600);
    const mins = Math.floor((diffSecs % 3600) / 60);
    const secs = diffSecs % 60;
    countdownText = `${hrs > 0 ? hrs + "h " : ""}${mins}m ${secs}s`;
  } else {
    // Find next HH day
    for (let offset = 1; offset <= 7; offset++) {
      const checkDay = (today + offset) % 7;
      if (hh.days.some(d => dayMap[d] === checkDay)) {
        const daysUntil = offset;
        const nextDayName = Object.entries(dayMap).find(([, v]) => v === checkDay);
        countdownText = `${daysUntil} dia${daysUntil > 1 ? "s" : ""} (${dayNames[nextDayName[0]]})`;
        break;
      }
    }
  }

  const scheduleDays = hh.days.map(d => dayNames[d] || d).join(", ");

  return (
    <div style={{
      maxWidth: "600px", margin: "0 auto", padding: "0 16px"
    }}>
      <div style={{
        background: "linear-gradient(135deg, #FFF8E6, #FFF0D0)", borderRadius: "12px",
        padding: "14px 18px", margin: "12px 0", border: "1px solid #F0E0C0",
        display: "flex", alignItems: "center", gap: "14px"
      }} data-testid="hh-banner">
        <div style={{ fontSize: "1.8rem", flexShrink: 0 }}>🍹</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: "0.85rem", color: "#D4772C" }}>Happy Hour no esta activo</div>
          <div style={{ fontSize: "0.75rem", color: "#8B7D6B", marginTop: "2px" }}>
            {scheduleDays} de {hh.start_time} a {hh.end_time}
          </div>
          {countdownText && (
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#D4772C", marginTop: "4px", fontVariantNumeric: "tabular-nums" }}>
              Inicia en: {countdownText}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== CLASSIC TEMPLATE (V1) ====================
const allergenMap = {
  "Gluten": { emoji: "🌾", color: "#D4A373" },
  "Lacteos": { emoji: "🥛", color: "#87CEEB" },
  "Huevo": { emoji: "🥚", color: "#FFD700" },
  "Pescado": { emoji: "🐟", color: "#4FC3F7" },
  "Mariscos": { emoji: "🦐", color: "#FF8A80" },
  "Frutos Secos": { emoji: "🥜", color: "#A1887F" },
};

function ClassicTemplate({ restaurant, notifications, bellBounce, pushToast, onDismissToast, mainCategories, mainItems, hhCategories, hhItems, hasHH, defaultTab }) {
  const [menuTab, setMenuTab] = useState(defaultTab);
  const categories = menuTab === "happy_hour" ? hhCategories : mainCategories;
  const items = menuTab === "happy_hour" ? hhItems : mainItems;
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [activeCategory, setActiveCategory] = useState(categories[0]?.id || null);
  const [showNotifs, setShowNotifs] = useState(false);

  // Reset category when switching tabs
  useEffect(() => { setActiveCategory(categories[0]?.id || null); setExpanded(null); setSearch(""); }, [menuTab]);

  const filtered = search
    ? items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()) || (i.description || "").toLowerCase().includes(search.toLowerCase()))
    : items;

  const scrollToSection = (catId) => {
    setActiveCategory(catId);
    const el = document.getElementById(`v1-section-${catId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Dynamic colors from restaurant config
  const rawAccent = restaurant.accent_color || "#D4772C";
  // Ensure price color is always visible on white bg - if accent is too light, use fallback
  const isLightColor = (hex) => {
    const c = hex.replace("#", "");
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 180;
  };
  const C = {
    primary: restaurant.primary_color || "#3E1F0D",
    accent: isLightColor(rawAccent) ? "#D4772C" : rawAccent,
    bg: restaurant.bg_color || "#F5EFE0",
    font: restaurant.font_family || "Manrope",
  };
  const logoSrc = restaurant.logo_url ? imgUrl(restaurant.logo_url) : null;

  const tabsRef = useRef(null);
  const handleWheelScroll = (e) => {
    if (tabsRef.current) {
      e.preventDefault();
      tabsRef.current.scrollLeft += e.deltaY;
    }
  };

  return (
    <div style={{ background: C.bg, minHeight: "100vh", fontFamily: `'${C.font}', sans-serif` }} data-testid="public-menu-classic">
      <PushToastBanner toast={pushToast} onDismiss={onDismissToast} />
      {/* Header Banner */}
      <div style={{
        background: `linear-gradient(180deg, ${C.primary} 0%, ${C.primary}cc 100%)`,
        padding: "24px 16px 36px", textAlign: "center", position: "relative",
        borderRadius: "0 0 50% 50% / 0 0 40px 40px"
      }}>
        {/* Bell - absolute positioned */}
        <button onClick={() => setShowNotifs(true)} className={bellBounce ? "bell-ring" : ""} style={{
          position: "absolute", top: "16px", right: "16px", zIndex: 2,
          background: "rgba(255,255,255,0.15)", border: "none", cursor: "pointer",
          borderRadius: "50%", width: "40px", height: "40px",
          display: "flex", alignItems: "center", justifyContent: "center"
        }} data-testid="notif-bell-btn">
          <Bell style={{ width: "20px", height: "20px", color: "#FDFBF7", fill: "#FDFBF7" }} />
          {notifications.length > 0 && <span style={{ position: "absolute", top: "2px", right: "2px", width: "10px", height: "10px", borderRadius: "50%", background: "#D94F3A", border: "2px solid " + C.primary }} />}
        </button>
        {logoSrc ? (
          <img src={logoSrc} alt={restaurant.name} loading="eager" decoding="async"
            style={{ width: "100px", height: "100px", objectFit: "contain", borderRadius: "20px", background: "rgba(255,255,255,0.1)", padding: "8px", margin: "0 auto", display: "block" }}
            data-testid="restaurant-logo" />
        ) : (
          <>
            <div style={{
              display: "inline-block", padding: "12px 32px", borderRadius: "8px",
              background: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.1)"
            }}>
              <h1 className="font-template" style={{ fontSize: "1.8rem", fontWeight: 700, color: "#FDFBF7", letterSpacing: "2px" }} data-testid="restaurant-name">
                Menú
              </h1>
            </div>
          </>
        )}
      </div>

      {/* Menu Tabs */}
      <div style={{ padding: "16px 16px 0", maxWidth: "600px", margin: "0 auto" }}>
        <MenuTabSwitcher activeTab={menuTab} onSwitch={setMenuTab} hasHH={hasHH} accentColor={C.accent} style="light" />
      </div>
      <HappyHourBanner restaurant={restaurant} activeTab={menuTab} />

      {/* Search Bar */}
      <div style={{ padding: "20px 16px 0", maxWidth: "600px", margin: "0 auto" }}>
        <div style={{ position: "relative" }}>
          <Search style={{ position: "absolute", left: "16px", top: "14px", width: "18px", height: "18px", color: "#A89F9A" }} />
          <input
            type="search" placeholder="Buscar en el menu..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%", padding: "14px 16px 14px 44px", borderRadius: "12px",
              border: "1px solid #E6DFD0", background: "white", color: C.primary,
              fontSize: "0.9rem", outline: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
            }}
            data-testid="menu-search"
          />
        </div>
      </div>

      {/* Category Tabs - STICKY */}
      <div style={{
        position: "sticky", top: 0, zIndex: 20, background: C.bg,
        padding: "12px 0",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
      }}>
        <div ref={tabsRef} onWheel={handleWheelScroll}
          style={{
          overflowX: "auto", display: "flex", gap: "8px",
          padding: "0 16px 6px", maxWidth: "600px", margin: "0 auto",
          WebkitOverflowScrolling: "touch", scrollbarWidth: "thin", scrollbarColor: `${C.accent}40 transparent`
        }}>
          {categories.map(cat => (
            <button key={cat.id} onClick={() => scrollToSection(cat.id)}
              style={{
                display: "flex", alignItems: "center", gap: "6px", padding: "8px 16px",
                borderRadius: "24px", border: "none", cursor: "pointer", whiteSpace: "nowrap",
                fontSize: "0.85rem", fontWeight: 600, flexShrink: 0, transition: "all 0.2s",
                background: activeCategory === cat.id ? C.primary : "transparent",
                color: activeCategory === cat.id ? "white" : "#6B5D50"
              }}
              data-testid={`tab-${cat.id}`}>
              <span>{cat.icon}</span> {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Allergen Legend */}
      <div style={{ padding: "20px 16px", maxWidth: "600px", margin: "0 auto" }}>
        <div style={{
          background: "white", borderRadius: "12px", padding: "16px 20px",
          border: "1px solid #E6DFD0", boxShadow: "0 1px 4px rgba(0,0,0,0.03)"
        }}>
          <h3 style={{ fontWeight: 700, fontSize: "1rem", color: C.primary, marginBottom: "12px", fontStyle: "italic" }}>
            Información de Alérgenos
          </h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 24px" }}>
            {Object.entries(allergenMap).map(([name, { emoji, color }]) => (
              <div key={name} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.8rem", color: "#4A3728" }}>
                <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: color, flexShrink: 0 }} />
                {emoji} {name}
              </div>
            ))}
          </div>
          <p style={{ fontSize: "0.7rem", color: "#A89F9A", marginTop: "10px" }}>
            Toca cada producto para ver sus alérgenos detallados. Si tienes alguna alergia, informa a nuestro personal.
          </p>
        </div>
      </div>

      {/* Menu Sections */}
      {categories.map((cat) => {
        const catItems = filtered.filter(i => i.category_id === cat.id);
        if (catItems.length === 0 && search) return null;
        return (
          <div key={cat.id} id={`v1-section-${cat.id}`} style={{ padding: "0 16px 32px", maxWidth: "600px", margin: "0 auto" }}
            data-testid={`menu-section-${cat.id}`}>
            {/* Section Header */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "1.8rem" }}>{cat.icon}</span>
                <h2 style={{
                  fontWeight: 900, fontSize: "1.4rem", color: C.primary,
                  textTransform: "uppercase", letterSpacing: "1px", fontFamily: "'Cabinet Grotesk', sans-serif"
                }}>{cat.name}</h2>
              </div>
              <div style={{ height: "3px", background: C.accent, marginTop: "8px", borderRadius: "2px" }} />
              {cat.description && (
                <p style={{ fontSize: "0.8rem", color: "#8B7D6B", fontStyle: "italic", marginTop: "8px" }}>{cat.description}</p>
              )}
            </div>

            {/* Items */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {catItems.map((item) => (
                <div key={item.id}
                  onClick={() => setExpanded(expanded === item.id ? null : item.id)}
                  style={{
                    background: "white", borderRadius: "12px", overflow: "hidden",
                    border: "1px solid #E6DFD0", cursor: "pointer", transition: "all 0.2s",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.03)"
                  }}
                  data-testid={`menu-item-${item.id}`}>
                  <div style={{ padding: "16px", display: "flex", gap: "14px", alignItems: "flex-start" }}>
                    {/* Image or Icon */}
                    {itemImg(item, 200) ? (
                      <img src={itemImg(item, 200)} alt={item.name} loading="lazy" decoding="async"
                        style={{ width: "72px", height: "72px", borderRadius: "10px", objectFit: "cover", flexShrink: 0 }} />
                    ) : (
                      <div style={{
                        width: "72px", height: "72px", borderRadius: "10px", flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: "#F5EFE0", fontSize: "2.2rem"
                      }}>
                        {item.icon || "🍽️"}
                      </div>
                    )}
                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <span style={{ fontWeight: 700, fontSize: "1rem", color: C.primary }}>{item.name}<UnitsBadge units={item.product_units} /></span>
                        <span style={{ fontWeight: 800, fontSize: "1rem", color: C.accent, marginLeft: "12px", whiteSpace: "nowrap" }}>
                          {fmtPrice(item.price)}
                        </span>
                      </div>
                      <p style={{ fontSize: "0.8rem", color: "#8B7D6B", marginTop: "4px", lineHeight: 1.5 }}>{item.description}</p>
                      <div style={{ display: "flex", gap: "6px", marginTop: "8px", flexWrap: "wrap" }}>
                        {(item.badges || []).map(b => (
                          <span key={b} style={{
                            padding: "2px 10px", borderRadius: "12px", fontSize: "0.7rem",
                            fontWeight: 700, background: C.accent, color: "white", textTransform: "uppercase"
                          }}>{b}</span>
                        ))}
                        {(item.allergens || []).map(a => {
                          const info = allergenMap[a] || { emoji: "⚠️", color: "#999" };
                          return (
                            <span key={a} style={{
                              padding: "2px 10px", borderRadius: "12px", fontSize: "0.7rem",
                              border: `1px solid ${info.color}`, color: "#4A3728",
                              display: "flex", alignItems: "center", gap: "3px"
                            }}>{info.emoji} {a}</span>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {expanded === item.id && (
                    <div style={{ borderTop: "1px dashed #E6DFD0", padding: "12px 16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem", marginBottom: "8px" }}>
                        <span style={{ color: "#8B7D6B" }}>Precio</span>
                        <span style={{ fontWeight: 800, color: C.accent }}>{fmtPrice(item.price)}</span>
                      </div>
                      {(item.allergens || []).length > 0 ? (
                        <div style={{
                          background: "#FFF8E1", padding: "10px 14px", borderRadius: "8px",
                          fontSize: "0.8rem", color: C.accent
                        }}>
                          <strong>Alérgenos:</strong>{" "}
                          {item.allergens.map(a => {
                            const info = allergenMap[a] || { emoji: "⚠️" };
                            return `${info.emoji} ${a}`;
                          }).join(", ")}
                        </div>
                      ) : (
                        <div style={{
                          background: "#E8F5E9", padding: "10px 14px", borderRadius: "8px",
                          fontSize: "0.8rem", color: "#2E7D32", fontWeight: 700
                        }}>
                          Sin alérgenos declarados
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <div style={{ textAlign: "center", padding: "24px", fontSize: "0.75rem", color: "#A89F9A" }}>
        {restaurant.name} &middot; Menu Digital
      </div>
      {showNotifs && <NotificationsModal notifications={notifications} onClose={() => setShowNotifs(false)} />}
    </div>
  );
}

// ==================== MODERN TEMPLATE (V2) ====================
function ModernTemplate({ restaurant, notifications, bellBounce, pushToast, onDismissToast, mainCategories, mainItems, hhCategories, hhItems, hasHH, defaultTab }) {
  const [menuTab, setMenuTab] = useState(defaultTab);
  const categories = menuTab === "happy_hour" ? hhCategories : mainCategories;
  const items = menuTab === "happy_hour" ? hhItems : mainItems;
  const [view, setView] = useState("home");
  const [selectedCat, setSelectedCat] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [search, setSearch] = useState("");
  const [catSearch, setCatSearch] = useState("");
  const [showNotifs, setShowNotifs] = useState(false);

  // Reset view when switching tabs
  useEffect(() => { setView("home"); setSelectedCat(null); setSelectedItem(null); setSearch(""); }, [menuTab]);

  const logoSrc = restaurant.logo_url ? imgUrl(restaurant.logo_url) : null;
  const rawAccent = restaurant.accent_color || "#D4772C";
  const isLightColor = (hex) => {
    const c = hex.replace("#", "");
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 180;
  };
  const C = {
    primary: restaurant.primary_color || "#3E1F0D",
    accent: isLightColor(rawAccent) ? "#D4772C" : rawAccent,
    bg: restaurant.bg_color || "#F0F0F0",
    font: restaurant.font_family || "Manrope",
  };

  const openCategory = (cat) => { setSelectedCat(cat); setCatSearch(""); setView("category"); };
  const goHome = () => { setView("home"); setSelectedCat(null); setSelectedItem(null); };

  const searchResults = search
    ? items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()) || (i.description || "").toLowerCase().includes(search.toLowerCase()))
    : [];

  const catSubtitles = ["Comienza aqui", "Nuestra especialidad", "Refrescantes", "Dulces tentaciones", "Lo mejor", "Para compartir"];

  // HOME VIEW
  if (view === "home") return (
    <div style={{ background: C.bg, minHeight: "100vh", fontFamily: `'${C.font}', sans-serif` }} data-testid="public-menu-modern">
      <PushToastBanner toast={pushToast} onDismiss={onDismissToast} />
      <div style={{ maxWidth: "600px", margin: "0 auto", padding: "0 16px" }}>
        {/* Top Bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {logoSrc ? (
              <img src={logoSrc} alt={restaurant.name} loading="eager" style={{ height: "48px", objectFit: "contain", borderRadius: "8px" }} data-testid="restaurant-logo" />
            ) : (
              <div style={{ padding: "8px 20px", borderRadius: "8px", background: C.primary }}>
                <span style={{ color: "white", fontWeight: 700, fontSize: "1rem", fontFamily: "'Cormorant Garamond', serif" }}>{restaurant.name}</span>
              </div>
            )}
          </div>
          <button onClick={() => setShowNotifs(true)} className={bellBounce ? "bell-ring" : ""} style={{
            position: "relative", background: "#FFF5E6", border: "none", cursor: "pointer", padding: "6px",
            borderRadius: "50%", width: "42px", height: "42px",
            display: "flex", alignItems: "center", justifyContent: "center"
          }} data-testid="notif-bell-btn">
            <Bell style={{ width: "22px", height: "22px", color: "#D4772C", fill: "#D4772C" }} />
            {notifications.length > 0 && <span style={{
              position: "absolute", top: "4px", right: "4px", width: "10px", height: "10px",
              borderRadius: "50%", background: "#D94F3A", border: "2px solid #F0F0F0"
            }} />}
          </button>
        </div>

        {/* Title */}
        <h1 style={{ fontSize: "1.8rem", fontWeight: 800, color: C.primary, margin: "8px 0 4px" }} data-testid="restaurant-name">
          Nuestro Menú
        </h1>
        <p style={{ fontSize: "0.9rem", color: "#8B7D6B", marginBottom: "16px" }}>Selecciona una categoria para explorar</p>

        {/* Menu Tabs */}
        <div style={{ marginBottom: "0" }}>
          <MenuTabSwitcher activeTab={menuTab} onSwitch={setMenuTab} hasHH={hasHH} accentColor={C.accent} style="light" />
        </div>
        <HappyHourBanner restaurant={restaurant} activeTab={menuTab} />

        {/* Search */}
        <div style={{ position: "relative", marginBottom: "20px" }}>
          <Search style={{ position: "absolute", left: "16px", top: "14px", width: "18px", height: "18px", color: "#A89F9A" }} />
          <input type="search" placeholder="Buscar platos, ingredientes..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{
              width: "100%", padding: "14px 16px 14px 44px", borderRadius: "12px",
              border: "1px solid #E6DFD0", background: "white", color: C.primary,
              fontSize: "0.9rem", outline: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
            }}
            data-testid="menu-search" />
        </div>

        {/* Search Results */}
        {search ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", paddingBottom: "24px" }}>
            {searchResults.length === 0 && <p style={{ textAlign: "center", padding: "20px", color: "#8B7D6B" }}>Sin resultados</p>}
            {searchResults.map(item => (
              <div key={item.id} onClick={() => setSelectedItem(item)}
                style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px", borderRadius: "12px", background: "white", cursor: "pointer", border: "1px solid #E6DFD0" }}>
                <MenuItemIcon icon={item.icon} imagePath={item.image_path} imageUrl={item.image_url} sizeClass="text-2xl" imgClass="w-12 h-12" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.9rem", color: C.primary }}>{item.name}<UnitsBadge units={item.product_units} /></div>
                  <div style={{ fontSize: "0.75rem", color: "#8B7D6B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.description}</div>
                </div>
                <span style={{ fontWeight: 800, color: C.accent, fontSize: "0.9rem" }}>{fmtPrice(item.price)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", paddingBottom: "24px" }}>
            {categories.map((cat, idx) => {
              const isLarge = idx < 2;
              const subtitle = cat.description || catSubtitles[idx % catSubtitles.length];
              if (!isLarge) return null;
              return (
                <div key={cat.id} onClick={() => openCategory(cat)}
                  style={{ position: "relative", borderRadius: "16px", overflow: "hidden", height: "200px", cursor: "pointer" }}
                  data-testid={`category-card-${cat.id}`}>
                  <img src={getCatImage(cat)} alt={cat.name} loading="eager"
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 60%)", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: "20px" }}>
                    <span style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: C.accent, marginBottom: "4px" }}>{subtitle}</span>
                    <span style={{ fontSize: "1.5rem", fontWeight: 800, color: "white" }}>{cat.name}</span>
                  </div>
                  <div style={{ position: "absolute", bottom: "20px", right: "20px", width: "32px", height: "32px", borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <ChevronRight style={{ width: "18px", height: "18px", color: "white" }} />
                  </div>
                </div>
              );
            })}
            {categories.filter((_, idx) => idx >= 2).length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                {categories.filter((_, idx) => idx >= 2).map((cat, i) => {
                  const realIdx = i + 2;
                  const subtitle = cat.description || catSubtitles[realIdx % catSubtitles.length];
                  return (
                    <div key={cat.id} onClick={() => openCategory(cat)}
                      style={{ position: "relative", borderRadius: "16px", overflow: "hidden", height: "160px", cursor: "pointer" }}
                      data-testid={`category-card-${cat.id}`}>
                      <img src={getCatImage(cat)} alt={cat.name} loading="lazy"
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 60%)", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: "14px" }}>
                        <span style={{ fontSize: "1.1rem", fontWeight: 800, color: "white" }}>{cat.name}</span>
                        <span style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.7)" }}>{subtitle}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div style={{ textAlign: "center", padding: "12px 0 24px", fontSize: "0.7rem", color: "#A89F9A" }}>
          {restaurant.name} &middot; Menu Digital
        </div>
      </div>
      {selectedItem && <ItemModal item={selectedItem} onClose={() => setSelectedItem(null)} />}
      {showNotifs && <NotificationsModal notifications={notifications} onClose={() => setShowNotifs(false)} />}
    </div>
  );

  // CATEGORY VIEW
  if (view === "category" && selectedCat) {
    const catItems = items.filter(i => i.category_id === selectedCat.id);
    const filteredCatItems = catSearch
      ? catItems.filter(i => i.name.toLowerCase().includes(catSearch.toLowerCase()) || (i.description || "").toLowerCase().includes(catSearch.toLowerCase()))
      : catItems;

    return (
      <div style={{ background: C.bg, minHeight: "100vh", fontFamily: `'${C.font}', sans-serif` }} data-testid="category-view">
        <div style={{ maxWidth: "600px", margin: "0 auto" }}>
          {/* Top bar: back + title + home */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 16px", borderBottom: "1px solid #E6DFD0", background: "white"
          }}>
            <button onClick={goHome} style={{
              background: "none", border: "none", cursor: "pointer", padding: "6px",
              color: C.primary, fontSize: "1.2rem", lineHeight: 1
            }} data-testid="back-btn">
              &#8249;
            </button>
            <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: C.primary, textAlign: "center" }}>{selectedCat.name}</h2>
            <button onClick={goHome} style={{
              width: "36px", height: "36px", borderRadius: "50%", border: "none", cursor: "pointer",
              background: "#E8F5E9", display: "flex", alignItems: "center", justifyContent: "center"
            }} data-testid="home-btn">
              <Home style={{ width: "18px", height: "18px", color: "#4A7c59" }} />
            </button>
          </div>

          {/* Search */}
          <div style={{ padding: "12px 16px" }}>
            <div style={{ position: "relative" }}>
              <Search style={{ position: "absolute", left: "16px", top: "14px", width: "18px", height: "18px", color: "#A89F9A" }} />
              <input type="search" placeholder="Buscar cortes, platos..."
                value={catSearch} onChange={e => setCatSearch(e.target.value)}
                style={{
                  width: "100%", padding: "14px 16px 14px 44px", borderRadius: "12px",
                  border: "1px solid #E6DFD0", background: "white", color: C.primary,
                  fontSize: "0.9rem", outline: "none"
                }}
                data-testid="category-search" />
            </div>
          </div>

          {/* Items */}
          <div style={{ padding: "0 16px 24px", display: "flex", flexDirection: "column", gap: "12px" }}>
            {filteredCatItems.map(item => (
              <div key={item.id} onClick={() => setSelectedItem(item)}
                style={{
                  display: "flex", alignItems: "center", gap: "14px", padding: "16px",
                  borderRadius: "14px", background: "white", cursor: "pointer",
                  border: "1px solid #E6DFD0", transition: "transform 0.15s"
                }}
                data-testid={`menu-item-${item.id}`}>
                {/* Icon/Image in circle bg */}
                {itemImg(item, 200) ? (
                  <img src={itemImg(item, 200)} alt={item.name} loading="lazy" decoding="async"
                    style={{ width: "56px", height: "56px", borderRadius: "14px", objectFit: "cover", flexShrink: 0 }} />
                ) : (
                  <div style={{
                    width: "56px", height: "56px", borderRadius: "14px", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "#FFF5E6", fontSize: "1.8rem"
                  }}>
                    {item.icon || "🍽️"}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <span style={{ fontWeight: 700, fontSize: "0.95rem", color: C.primary }}>{item.name}<UnitsBadge units={item.product_units} /></span>
                    <span style={{ fontWeight: 800, fontSize: "0.95rem", color: C.accent, marginLeft: "12px", whiteSpace: "nowrap" }}>{fmtPrice(item.price)}</span>
                  </div>
                  <p style={{ fontSize: "0.78rem", color: "#8B7D6B", marginTop: "3px", lineHeight: 1.4 }}>{item.description}</p>
                  {((item.badges || []).length > 0 || (item.allergens || []).length > 0) && (
                    <div style={{ display: "flex", gap: "5px", marginTop: "6px", flexWrap: "wrap" }}>
                      {(item.badges || []).map(b => (
                        <span key={b} style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "0.65rem", fontWeight: 700, background: C.accent, color: "white" }}>{b}</span>
                      ))}
                      {(item.allergens || []).map(a => (
                        <span key={a} style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "0.65rem", border: "1px solid #E6DFD0", color: "#8B7D6B" }}>{a}</span>
                      ))}
                    </div>
                  )}
                </div>
                <ChevronRight style={{ width: "16px", height: "16px", color: "#C0C0C0", flexShrink: 0 }} />
              </div>
            ))}
            {filteredCatItems.length === 0 && catSearch && (
              <p style={{ textAlign: "center", padding: "24px", color: "#8B7D6B" }}>Sin resultados</p>
            )}
          </div>
        </div>
        {selectedItem && <ItemModal item={selectedItem} onClose={() => setSelectedItem(null)} />}
      </div>
    );
  }

  return null;
}

// ==================== NOTIFICATIONS MODAL ====================
function NotificationsModal({ notifications, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "flex-end", justifyContent: "center" }} data-testid="notif-modal">
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} onClick={onClose} />
      <div style={{
        position: "relative", width: "100%", maxWidth: "440px", maxHeight: "80vh", overflowY: "auto",
        background: "white", borderRadius: "24px 24px 0 0", padding: "0"
      }}>
        {/* Handle */}
        <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 0" }}>
          <div style={{ width: "40px", height: "4px", borderRadius: "2px", background: "#D0D0D0" }} />
        </div>

        <h2 style={{ fontSize: "1.15rem", fontWeight: 800, textAlign: "center", margin: "12px 0 20px", color: "#2A2A2A" }}>
          Notificaciones e Información
        </h2>

        <div style={{ padding: "0 24px" }}>
          {notifications.length === 0 && (
            <p style={{ textAlign: "center", color: "#8B7D6B", padding: "20px 0" }}>No hay notificaciones en este momento.</p>
          )}
          {notifications.map(n => (
            <div key={n.id} style={{
              display: "flex", alignItems: "flex-start", gap: "14px", padding: "14px 0",
              borderBottom: "1px solid #F0F0F0"
            }}>
              <div style={{
                width: "44px", height: "44px", borderRadius: "50%", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "#FFF5E6", fontSize: "1.3rem"
              }}>
                {n.icon || "📢"}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#2A2A2A" }}>{n.title}</div>
                <div style={{ fontSize: "0.8rem", color: "#8B7D6B", marginTop: "2px" }}>{n.description}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Close */}
        <div style={{ padding: "20px 24px 24px" }}>
          <button onClick={onClose}
            style={{
              width: "100%", padding: "14px", borderRadius: "12px", border: "none",
              background: "#2A2A2A", color: "white", fontSize: "0.95rem",
              fontWeight: 700, letterSpacing: "1px", cursor: "pointer", textTransform: "uppercase"
            }}
            data-testid="close-notif-modal-btn">
            CERRAR
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== ITEM DETAIL MODAL ====================
const allergenMapModal = {
  "Gluten": "🌾", "Lacteos": "🥛", "Huevo": "🥚", "Pescado": "🐟", "Mariscos": "🦐", "Frutos Secos": "🥜",
};

function ItemModal({ item, onClose }) {
  const src = itemImg(item, 600);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, display: "flex", alignItems: "flex-end", justifyContent: "center" }} data-testid="item-modal">
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} onClick={onClose} />
      <div style={{
        position: "relative", width: "100%", maxWidth: "440px", maxHeight: "85vh", overflowY: "auto",
        background: "white", borderRadius: "24px 24px 0 0", padding: "0"
      }}>
        {/* Handle bar */}
        <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 0" }}>
          <div style={{ width: "40px", height: "4px", borderRadius: "2px", background: "#D0D0D0" }} />
        </div>

        {/* Image */}
        {src ? (
          <div style={{ padding: "12px 20px 0" }}>
            <img src={src} alt={item.name} loading="eager"
              style={{ width: "100%", height: "240px", objectFit: "cover", borderRadius: "16px" }} />
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "24px 0 0" }}>
            <span style={{ fontSize: "5rem" }}>{item.icon || "🍽️"}</span>
          </div>
        )}

        {/* Content */}
        <div style={{ padding: "20px 24px 0" }}>
          <h3 style={{ fontSize: "1.4rem", fontWeight: 800, textAlign: "center", color: "#2A2A2A" }}>{item.name}<UnitsBadge units={item.product_units} /></h3>
          <p style={{ fontSize: "1.3rem", fontWeight: 800, textAlign: "center", color: "#D4772C", margin: "6px 0 0" }}>
            {fmtPrice(item.price)}
          </p>
          <p style={{ fontSize: "0.9rem", color: "#6B6A65", textAlign: "center", margin: "12px 0 0", lineHeight: 1.6 }}>
            {item.description}
          </p>
        </div>

        {/* Badges */}
        {(item.badges || []).length > 0 && (
          <div style={{ display: "flex", gap: "8px", justifyContent: "center", padding: "12px 24px 0", flexWrap: "wrap" }}>
            {item.badges.map(b => (
              <span key={b} style={{ padding: "4px 14px", borderRadius: "20px", fontSize: "0.8rem", fontWeight: 700, background: "#D4772C", color: "white" }}>{b}</span>
            ))}
          </div>
        )}

        {/* Allergens */}
        {(item.allergens || []).length > 0 && (
          <div style={{ padding: "16px 24px 0" }}>
            <p style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#D4772C", marginBottom: "8px" }}>Alérgenos</p>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {item.allergens.map(a => (
                <span key={a} style={{
                  padding: "6px 14px", borderRadius: "20px", fontSize: "0.8rem", fontWeight: 500,
                  background: "#FFF8F0", border: "1px solid #F0E6D8", color: "#4A3728",
                  display: "flex", alignItems: "center", gap: "4px"
                }}>
                  {allergenMapModal[a] || "⚠️"} {a}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Close Button */}
        <div style={{ padding: "20px 24px 24px" }}>
          <button onClick={onClose}
            style={{
              width: "100%", padding: "14px", borderRadius: "12px", border: "none",
              background: "#2A2A2A", color: "white", fontSize: "0.95rem",
              fontWeight: 700, letterSpacing: "1px", cursor: "pointer", textTransform: "uppercase"
            }}
            data-testid="close-modal-btn">
            CERRAR
          </button>
        </div>
      </div>
    </div>
  );
}
